import clsx from 'clsx'
import React, { FC, useCallback, useContext, useEffect, useState } from 'react'

import { BraintreeContext } from '@Domains/Checkout/Providers/BraintreeContextProvider'
import { CheckoutFlowContext } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/CheckoutFlowContext'
import { CheckoutFlowStep, CheckoutFlowSteps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
// eslint-disable-next-line max-len
import { ApplePayButton } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Braintree/Components/ApplePay/Components/ApplePayButton'
import {
  useBraintreeCheckoutResponse,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Braintree/Hooks/useBraintreeCheckoutResponse'
import { CheckoutPaymentComponentProps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Hooks/usePaymentComponentFactory'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './BraintreeApplePay.module.scss'

interface AppleSessionProps {
  STATUS_FAILURE: 1
  STATUS_INVALID_BILLING_POSTAL_ADDRESS: 2
  STATUS_INVALID_SHIPPING_CONTACT: 4
  STATUS_INVALID_SHIPPING_POSTAL_ADDRESS: 3
  STATUS_PIN_INCORRECT: 6
  STATUS_PIN_LOCKOUT: 7
  STATUS_PIN_REQUIRED: 5
  STATUS_SUCCESS: 0
  abort: Function
  begin: Function
  completeCouponCodeChange: Function
  completeMerchantValidation: Function
  completePayment: Function
  completePaymentMethodSelection: Function
  completeShippingContactSelection: Function
  completeShippingMethodSelection: Function
  oncancel: null | Function
  oncouponcodechanged: null | Function
  onpaymentauthorized: Function
  onpaymentmethodselected: null | Function
  onshippingcontactselected: null | Function
  onshippingmethodselected: null | Function
  onvalidatemerchant: Function

}

interface ApplePayProps {
  createPaymentRequest: Function

  performValidation: Function

  teardown: Function

  tokenize: Function
}

export const BraintreeApplePayButton: FC<CheckoutPaymentComponentProps> = ({
  grandTotal,
  option,
  submitPayment,
}) => {
  const {
    setActiveStep,
    stepValidations,
  } = useContext(CheckoutFlowContext)
  const {
    braintreeClient,
    braintreeInstance,
  } = useContext(BraintreeContext)
  const { handleBraintreePaymentSubmitResponse } = useBraintreeCheckoutResponse()
  const [mounted, setMounted] = useState(false)
  const [applePay, setApplePay] = useState<ApplePayProps | null>(null)

  useEffect(() => {
    if (!mounted && !!braintreeInstance?.applePay && !!braintreeClient) {
      setMounted(true)
      braintreeInstance.applePay.create({
        client: braintreeClient,
      }, (applePayErr, applePayInstance: any) => {
        if (applePayErr) {
          console.warn('Error creating Braintree applePay Checkout:', applePayErr)
          return
        }
        setApplePay(applePayInstance)
      })
    }
  }, [braintreeClient, braintreeInstance, mounted])

  const payWithApples = useCallback(() => {
    if (!applePay) {
      return
    }

    const paymentRequest = applePay.createPaymentRequest({
      total: {
        label: 'Trendhim',
        amount: grandTotal?.value?.plain,
      },
    })
    const session: AppleSessionProps | null = (typeof window !== 'undefined' && (window as any).ApplePaySession
      ? new (window as any).ApplePaySession(3, paymentRequest)
      : null)

    if (!session) {
      console.warn('Apple Pay failed to create session')
    }
    session!.onvalidatemerchant = (event) => {
      applePay.performValidation({
        validationURL: event.validationURL,
        displayName: 'Trendhim',
      }, (err, merchantSession) => {
        if (err) {
          console.error('Apple Pay failed to load.')
          return
        }
        session!.completeMerchantValidation(merchantSession)
      })
    }
    session!.onpaymentauthorized = (event: { payment: { token: string } }) => {
      applePay.tokenize({
        token: event.payment.token,
      }, (tokenizeErr, payload) => {
        if (tokenizeErr) {
          console.error('Error tokenizing Apple Pay:', tokenizeErr)
          session!.completePayment(session!.STATUS_FAILURE)
          return
        }
        submitPayment({
          formData: payload,
          isValid: true,
          type: option.type,
        }, res => handleBraintreePaymentSubmitResponse(option.type, res))
        session!.completePayment(session!.STATUS_SUCCESS)
      })
    }
    session!.begin()
  }, [applePay, handleBraintreePaymentSubmitResponse, option.type, submitPayment, grandTotal])

  return (
    <div className={styles.applePayButtonWrapper}>
      {!(stepValidations[CheckoutFlowStep.ADDRESS] && stepValidations[CheckoutFlowStep.SHIPPING])
        && <div className={styles.buttonDisableLayer} onClick={() => setActiveStep(CheckoutFlowSteps.SUCCESS)} />}
      {!applePay && <Spinner />}
      {applePay && (
        <ApplePayButton
          className={clsx(
            styles.appleButton,
            !(stepValidations[CheckoutFlowStep.ADDRESS] && stepValidations[CheckoutFlowStep.SHIPPING]) && styles.disabled,
          )}
          onClick={payWithApples}
        />
      )}
    </div>
  )
}
